// v2/frontend/src/stores/locationStore.ts
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Location } from '../types';

interface LocationState {
  currentLocation: Location | null;
  locations: Location[];
}

interface LocationActions {
  setCurrentLocation: (location: Location | null) => void;
  setLocations: (locations: Location[]) => void;
  clearLocation: () => void;
  validateCurrentLocation: () => void;
}

type LocationStore = LocationState & LocationActions;

const useLocationStore = create<LocationStore>()(
  persist(
    (set, get) => ({
      currentLocation: null,
      locations: [],

      setCurrentLocation: (location: Location | null) => {
        set({ currentLocation: location });
      },

      setLocations: (locations: Location[]) => {
        // Defensive check: ensure locations is an array
        const safeLocations = Array.isArray(locations) ? locations : [];
        set({ locations: safeLocations });

        const { currentLocation } = get();

        // If we have a current location, check if it still exists in the new list
        if (currentLocation) {
          const stillExists = safeLocations.some(loc => loc.id === currentLocation.id);
          if (!stillExists) {
            // Current location no longer exists - clear it and select first available
            set({ currentLocation: safeLocations.length > 0 ? safeLocations[0] : null });
            return;
          }
        }

        // Auto-select first location if none selected
        if (!currentLocation && safeLocations.length > 0) {
          set({ currentLocation: safeLocations[0] });
        }
      },

      clearLocation: () => {
        set({ currentLocation: null, locations: [] });
      },

      validateCurrentLocation: () => {
        const { currentLocation, locations } = get();
        if (currentLocation && locations.length > 0) {
          const stillExists = locations.some(loc => loc.id === currentLocation.id);
          if (!stillExists) {
            set({ currentLocation: locations[0] });
          }
        }
      },
    }),
    {
      name: 'veloce-location',
      partialize: (state) => ({
        currentLocation: state.currentLocation,
      }),
    }
  )
);

export default useLocationStore;
